import { Platform, AppState, AppStateStatus } from 'react-native';
import { Analytics, MediaConstraints } from '@matap/shared';
import { Stream } from './stream';
import { block } from './utils/blocker';
import { DeviceManager } from './device-manager/device.manager';

enum FacingEnum {
  USER = 'user',
  ENVIRONMENT = 'environment',
}

export class PeerLocalStream extends Stream<'change' | 'analytic'> {
  private readonly constraints: MediaConstraints & { audio: { deviceId?: any }; video: { deviceId?: any; facingMode?: FacingEnum } };
  private isSwitchingMedia = false;
  private muted = false;

  // State is now properly encapsulated inside the instance
  private activeStream: MediaStream | null = null;

  // Background state management
  private appStateSubscription: any;
  private wasVideoEnabledBeforeBackground = true;

  constructor(
    constraints: MediaConstraints,
    private readonly sessionId: string,
    private readonly deviceManager: DeviceManager,
  ) {
    super();
    // Deep clone to prevent mutating the original objects passed by reference
    this.constraints = JSON.parse(JSON.stringify(constraints || {}));

    if (this.constraints.video && typeof this.constraints.video === 'boolean' && this.constraints.video === true) {
      this.constraints.video = { facingMode: FacingEnum.USER };
    }
    if (this.constraints.audio && typeof this.constraints.audio === 'boolean' && this.constraints.audio === true) {
      this.constraints.audio = {};
    }
    this.deviceManager.on('change', this.mDevicesListener);

    // 1. Add the AppState listener for native platforms
    if (Platform.OS !== 'web') {
      this.appStateSubscription = AppState.addEventListener('change', this.handleAppStateChange);
    }
  }

  // 2. Handle the background / foreground transitions
  private handleAppStateChange = (nextAppState: AppStateStatus) => {
    if (this.isDestroyedOrDestroying()) return;

    const stream = this.stream();
    if (!stream) return;

    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack) return;

    if (nextAppState.match(/inactive|background/)) {
      // App went to the background.
      // Save their current preference (did they already have the camera off?)
      this.wasVideoEnabledBeforeBackground = videoTrack.enabled;

      // Force disable the hardware track so the OS doesn't crash the app
      videoTrack.enabled = false;
      this.emit('change', stream); // Update UI to show paused video avatar
    } else if (nextAppState === 'active') {
      // App came back to the foreground.
      // Only turn the camera back on if they had it on before leaving!
      if (this.wasVideoEnabledBeforeBackground) {
        videoTrack.enabled = true;
      }
      this.emit('change', stream);
    }
  };

  private mDevicesListener = async () => {
    // Safely extract the string ID (handles both '123' and { exact: '123' })
    const currentMicId = typeof this.constraints.audio?.deviceId === 'object' ? this.constraints.audio.deviceId.exact : this.constraints.audio?.deviceId;

    if (currentMicId) {
      if (!this.deviceManager.availableMicDevices.find((d) => d.deviceId === currentMicId)) {
        this.constraints.audio.deviceId = this.deviceManager.availableMicDevices[0]?.deviceId;
        await this.switchInputDevice('audio');
      }
    }

    const currentCamId = typeof this.constraints.video?.deviceId === 'object' ? this.constraints.video.deviceId.exact : this.constraints.video?.deviceId;

    if (currentCamId) {
      if (!this.deviceManager.availableCameraDevices.find((d) => d.deviceId === currentCamId)) {
        this.constraints.video.deviceId = this.deviceManager.availableCameraDevices[0]?.deviceId;
        await this.switchInputDevice('video');
      }
    }
  };

  public setSpeakerDevice(deviceId: string) {
    return this.deviceManager.setSpeakerDevice(deviceId);
  }

  private async switchInputDevice(kind: 'audio' | 'video') {
    const stream = this.stream();
    if (this.isDestroyedOrDestroying() || !stream || this.isSwitchingMedia) {
      return false;
    }

    this.isSwitchingMedia = true;
    const oldTrack = (kind === 'audio' ? stream.getAudioTracks() : stream.getVideoTracks())[0];

    // Request ONLY the kind of media we are trying to swap
    const specificConstraints = {
      audio: kind === 'audio' ? this.constraints.audio : false,
      video: kind === 'video' ? this.constraints.video : false,
    };

    try {
      const tempStream = await navigator.mediaDevices.getUserMedia(specificConstraints);
      if (this.isDestroyedOrDestroying()) {
        tempStream?.getTracks().forEach((t) => t.stop());
        return false;
      }

      const newTrack = (kind === 'audio' ? tempStream.getAudioTracks() : tempStream.getVideoTracks())[0];

      if (!newTrack) {
        tempStream?.getTracks().forEach((t) => t.stop());
        return false;
      }

      // Seamlessly swap tracks in the existing stream
      if (oldTrack) {
        stream.removeTrack(oldTrack);
        oldTrack.stop(); // Only stop the track we are replacing!
      }
      stream.addTrack(newTrack);

      // Persist mute state if swapping microphones
      if (kind === 'audio' && this.isMuted()) {
        newTrack.enabled = false;
      }

      this.emit('change', this.stream(), oldTrack, newTrack);
      return true;
    } catch (e) {
      console.error(`[PeerLocalStream] Failed to switch ${kind}:`, e);

      // Restore the constraints object back to the old track's device ID to keep state synchronized
      if (oldTrack) {
        const oldDeviceId = oldTrack.getSettings().deviceId;
        if (kind === 'audio') {
          this.constraints.audio = { ...(typeof this.constraints.audio === 'object' ? this.constraints.audio : {}), deviceId: { exact: oldDeviceId } };
        } else {
          this.constraints.video = { ...(typeof this.constraints.video === 'object' ? this.constraints.video : {}), deviceId: { exact: oldDeviceId } };
        }
      }

      this.emit('error', e);
      return false;
    } finally {
      this.isSwitchingMedia = false;
    }
  }

  public async setMicDevice(deviceId: string) {
    // Preserve existing constraints using spread operator
    this.constraints.audio = {
      ...(typeof this.constraints.audio === 'object' ? this.constraints.audio : {}),
      deviceId: { exact: deviceId },
    };

    return this.switchInputDevice('audio');
  }

  public async setCameraDevice(deviceId: string) {
    if (!this.constraints.video) {
      return;
    }
    // Preserve existing constraints using spread operator
    this.constraints.video = {
      ...(typeof this.constraints.video === 'object' ? this.constraints.video : {}),
      deviceId: { exact: deviceId },
    };

    return this.switchInputDevice('video');
  }

  public async switchCamera() {
    const stream = this.stream();
    if (this.isDestroyedOrDestroying() || !stream || this.isSwitchingMedia) {
      return;
    }
    if (!this.constraints.video) {
      return;
    }
    if (Platform.OS === 'web') {
      delete this.constraints.video.deviceId;
      this.constraints.video.facingMode = this.constraints.video.facingMode === FacingEnum.USER ? FacingEnum.ENVIRONMENT : FacingEnum.USER;
      await this.switchInputDevice('video');
    } else {
      stream.getVideoTracks().forEach((track) => {
        // @ts-ignore
        track._switchCamera?.();

        if (typeof this.constraints.video === 'object') {
          this.constraints.video.facingMode = this.constraints.video.facingMode === FacingEnum.USER ? FacingEnum.ENVIRONMENT : FacingEnum.USER;
        }
      });
    }
  }

  public toggleMute() {
    const localStream = this.stream();
    if (this.isDestroyedOrDestroying() || !localStream) {
      return;
    }
    this.muted = !this.muted;
    const track = localStream.getTracks().find((t) => t.kind === 'audio');
    if (track) {
      track.enabled = !this.muted;

      // Explicitly emit analytic event because track.enabled does not fire native 'mute' event
      this.emit(
        'analytic',
        new Analytics.TrackStateChange(this.sessionId, {
          kind: 'audio',
          local: true,
          enabled: !this.muted,
        }),
      );
    }

    this.emit('change', this.stream());
  }

  public isMuted() {
    return this.muted;
  }

  public stream(): MediaStream {
    return this.activeStream as MediaStream;
  }

  public getSelectedMic() {
    return this.stream()?.getAudioTracks()[0];
  }

  public getSelectedCamera() {
    return this.stream()?.getVideoTracks()[0];
  }

  public initialize() {
    if (this.activeStream || this.isDestroyedOrDestroying()) {
      return;
    }
    const fn = async () => {
      if (this.activeStream || this.isDestroyedOrDestroying()) {
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia(this.constraints);
      if (this.isDestroyedOrDestroying()) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      stream.getTracks().forEach((t) => {
        this.emit('analytic', new Analytics.TrackCreated(this.sessionId, { kind: t.kind as any, local: true }));

        t.addEventListener('mute', () => this.emit('analytic', new Analytics.TrackStateChange(this.sessionId, { kind: t.kind as any, local: true, enabled: false })));
        t.addEventListener('unmute', () => this.emit('analytic', new Analytics.TrackStateChange(this.sessionId, { kind: t.kind as any, local: true, enabled: true })));
        t.addEventListener('ended', () => this.emit('analytic', new Analytics.TrackStopped(this.sessionId, { kind: t.kind as any, local: true })));
      });

      this.activeStream = stream;

      if (this.isMuted()) {
        this.muted = false;
        this.toggleMute();
      } else {
        this.emit('change', this.stream());
      }
    };

    return new Promise((resolve, reject) => {
      block('local-stream-getter', () =>
        fn()
          .then(resolve)
          .catch((e) => {
            this.emit('analytic', new Analytics.Error(this.sessionId, 'getUserMedia()', e));
            this.emit('error', e);
            reject(e);
          }),
      );
    });
  }

  protected onDestroy() {
    this.deviceManager.off('change', this.mDevicesListener);

    // 3. Clean up the AppState listener
    if (this.appStateSubscription) {
      this.appStateSubscription.remove();
    }

    if (this.activeStream) {
      this.activeStream.getTracks().forEach((t) => {
        t.stop();
        this.emit(
          'analytic',
          new Analytics.TrackStopped(this.sessionId, {
            kind: t.kind as any,
            local: false,
          }),
        );
      });
      this.activeStream = null;
    }
  }
}
